import { useState } from 'react'
import { Dices, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { useAppEvent } from '../../AppContext.tsx'
import { CollapsibleGroup } from '../Collapsible.tsx'
import { flash } from '../../toast.ts'

/**
 * Batch ("seed sweep") runs: run the active grammar to completion once per
 * seed and keep every output so the user can flip through them. Seeds are
 * either a consecutive RANGE (reproducible) or freshly RANDOM (exploration).
 * Lives in the left run rail; the engine work is in AppState.runBatch.
 */
export function BatchRun() {
  const app = useAppEvent('batch')
  const [collapsed, setCollapsed] = useState(true)
  const [count, setCount] = useState(8)
  const [mode, setMode] = useState<'range' | 'random'>('range')
  const [start, setStart] = useState(() => app.grammar.config.seed)

  const n = Math.max(1, Math.min(500, Math.floor(count) || 1))

  const run = () => {
    const t0 = performance.now()
    const done = app.runBatch({ count: n, mode, start })
    flash(`Batch , ${done} run${done === 1 ? '' : 's'} in ${(performance.now() - t0).toFixed(0)} ms`)
  }

  const runs = app.batchRuns
  const idx = app.batchIndex
  const cur = runs[idx]

  return (
    <section className="panel-section batch-run">
      <CollapsibleGroup
        title="Batch run"
        collapsed={collapsed}
        onToggle={() => setCollapsed((c) => !c)}
        right={
          app.batchActive ? (
            <span className="group-count" title={`${runs.length} batch runs loaded`}>
              {runs.length}
            </span>
          ) : undefined
        }
      >
        <div className="settings-body">
          <div className="field-grid">
            <label className="field">
              <span className="field-label">Runs</span>
              <input type="number" min={1} max={500} value={count} onChange={(e) => setCount(Number(e.target.value))} />
            </label>
            <label className="field">
              <span className="field-label">Seeds</span>
              <select value={mode} onChange={(e) => setMode(e.target.value as 'range' | 'random')}>
                <option value="range">Range</option>
                <option value="random">Random</option>
              </select>
            </label>
            {mode === 'range' && (
              <label className="field">
                <span className="field-label">Start seed</span>
                <input type="number" value={start} onChange={(e) => setStart(Number(e.target.value) || 0)} />
              </label>
            )}
          </div>
          <p className="section-hint">
            {mode === 'range' ? (
              <>
                Runs seeds <strong>{start}</strong>–<strong>{start + n - 1}</strong> , reproducible.
              </>
            ) : (
              <>
                Picks <strong>{n}</strong> random seeds , broader exploration.
              </>
            )}
          </p>
          <button className="primary small batch-go" onClick={run}>
            <Dices size={14} /> Run {n} {mode === 'random' ? 'random' : 'seeded'} run{n === 1 ? '' : 's'}
          </button>

          {app.batchActive && cur && (
            <div className="batch-results">
              <div className="batch-nav">
                <button className="icon-btn" title="Previous run" onClick={() => app.prevBatchRun()}>
                  <ChevronLeft size={16} />
                </button>
                <span className="batch-pos">
                  Run {idx + 1}/{runs.length}
                </span>
                <button className="icon-btn" title="Next run" onClick={() => app.nextBatchRun()}>
                  <ChevronRight size={16} />
                </button>
                <button className="icon-btn batch-clear" title="Clear batch results" onClick={() => app.clearBatch()}>
                  <X size={15} />
                </button>
              </div>
              <div className="batch-meta">
                seed <strong>{cur.seed}</strong> · {cur.nodes}n/{cur.edges}e · {cur.steps} steps
              </div>
              <div className="batch-chips">
                {runs.map((r, i) => (
                  <button
                    key={i}
                    className={'batch-chip' + (i === idx ? ' active' : '')}
                    title={`Run ${i + 1} , seed ${r.seed} · ${r.nodes}n/${r.edges}e · ${r.steps} steps`}
                    onClick={() => app.showBatchRun(i)}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </CollapsibleGroup>
    </section>
  )
}
